import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useHits,
  useInstantSearch,
  useSearchBox,
  UseSearchBoxProps,
  InstantSearch,
  Configure as SearchConfig,
} from 'react-instantsearch';
import Button from 'components/button';
import SearchButtonSVG from 'shared/assets/icons/search.svg';
import { Container, Flex } from 'components/box';
import { css } from '@emotion/react';
import 'instantsearch.css/themes/reset.css';
import 'instantsearch.css/themes/satellite.css';
import { Box, Grid } from 'components/box';
import { X as XIcon } from 'react-feather';
import { Text, Heading } from 'components/typography';
import makeResizedUrl from '../../../../util/make-url';
import { Link } from 'components/configurable-routing';
import { productHoverStyle } from 'components/_shared/widgets/gallery/shared';
import { SearchFilters, FilterIcon, SearchFiltersMobile } from './filters';
import RenderControl from 'components/_shared/widgets/render-control';
import { soldOutWrapperStyle } from 'shared/style';
import SoldOutMask from 'components/sold-out-mask';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { useEffect } from 'react';
import config from '../../../../util/load-config';
import Loading from 'components/loading';
import CustomPagination from 'components/_shared/widgets/search/custom-pagination';
import {
  AlgoliaSearchResult,
  isValidHit,
  SearchResult,
  transformSearchResult,
} from './types';
import { zIndices } from 'constants/z-indices';
import { UnbxdAnalyticsIdentifiers } from 'constants/netcore';
import { liteClient } from 'algoliasearch/lite';
import SearchErrorFallback from 'components/_shared/widgets/search/error-fallback';
import ErrorBoundary from 'components/_global/error-boundary';
import { HEADER_SPACING_HEIGHT_W_SEARCH } from 'constants/header';
import { useGetTokens, usePostInsights } from 'hooks/algolia';

// TODO: Extract into separate component files. Leaving as is now for diff's sake

const SEARCH_DEBOUNCE_TIMEOUT = 1200;
const FADE_ANIMATION_DURATION = 0.3;
const PLACEHOLDER_OPACITY = 0.64;

let debounceTimerId: NodeJS.Timeout | undefined;

// debounce search request to lower costs
const queryHook = (query: string, search: (value: string) => void) => {
  if (debounceTimerId) {
    clearTimeout(debounceTimerId);
  }

  debounceTimerId = setTimeout(() => search(query), SEARCH_DEBOUNCE_TIMEOUT);
};

const MAX_PRODUCT_IMG_SIZE = 200;

export const SearchBar = ({
  isSearchActive,
  openSearch,
  closeSearch,
  ...rest
}: UseSearchBoxProps & {
  isSearchActive?: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}) => {
  const { query, refine, clear } = useSearchBox({ ...rest, queryHook });
  const { status } = useInstantSearch();
  const [inputValue, setInputValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);

  const setQuery = useCallback(
    (newQuery: string) => {
      setInputValue(newQuery);

      refine(newQuery);
    },
    [refine]
  );

  const resetSearchField = useCallback(() => {
    setQuery('');
    clear();
  }, [clear, setQuery]);

  // just to make double sure that we don't get weird state mismatches
  useEffect(() => {
    if (!isSearchActive) {
      resetSearchField();
    }
  }, [isSearchActive, resetSearchField]);

  return (
    <Flex
      position="relative"
      alignContent="center"
      height="32px"
      css={css`
        .close-hidden {
          transition: opacity ${FADE_ANIMATION_DURATION - 0.2}s ease-in-out 0s;
        }
        .close-visible {
          transition: opacity ${FADE_ANIMATION_DURATION}s ease-in-out
            ${FADE_ANIMATION_DURATION}s;
        }
      `}
    >
      <Flex position="absolute" mt="6px" left={2}>
        <SearchButtonSVG
          css={theme => css`
            stroke: ${isSearchActive
              ? theme.colors.darkishGrey
              : theme.colors.white};
            width: 20px;
            height: 20px;
            stroke-width: 3px;
            z-index: ${zIndices.TWENTY_ABOVE};
            opacity: ${PLACEHOLDER_OPACITY};
          `}
        />
      </Flex>
      <Flex
        className={isSearchActive ? 'close-visible' : 'close-hidden'}
        position="absolute"
        mt="6px"
        right={1}
        backgroundColor="white"
        opacity={isSearchActive ? 1 : 0}
        tabIndex={isSearchActive ? 0 : -1}
      >
        <Button
          variant="minimal"
          onClick={() => {
            resetSearchField();
            closeSearch();
          }}
        >
          <XIcon
            color="grey"
            css={css`
              background-color: transparent;
            `}
          />
        </Button>
      </Flex>
      <Box width="100%">
        <form
          action=""
          role="search"
          noValidate
          onSubmit={event => {
            event.preventDefault();
            event.stopPropagation();

            if (inputRef.current) {
              inputRef.current.blur();
            }
          }}
          onReset={event => {
            event.preventDefault();
            event.stopPropagation();

            resetSearchField();

            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <input
            id="search-box"
            ref={inputRef}
            autoComplete="off"
            autoCorrect="off"
            onFocus={openSearch}
            autoCapitalize="off"
            placeholder="Search"
            spellCheck={false}
            maxLength={512}
            type="search"
            value={inputValue}
            onChange={event => {
              setQuery(event.currentTarget.value);
            }}
            css={theme => css`
              @media ${theme.mediaQueries.mobileOnly} {
                font-size: 16px;
                padding: 6px;
                padding-left: 35px;
              }
              border-radius: 8px;
              background-color: ${isSearchActive
                ? 'white'
                : 'rgba(256, 256, 256, 0.2)'};
              color: ${theme.colors.darkestGrey};
              border: none;
              padding: 8px;
              padding-left: 35px;
              width: 100%;
              ::placeholder {
                color: ${isSearchActive ? 'unset' : theme.colors.white};
                opacity: ${PLACEHOLDER_OPACITY};
              }
              ::after {
                display: none;
              }
              ${isSearchActive &&
              css`
                box-shadow: 0px 8px 16px 0px #0000001f;
              `}
              transition: background-color ${FADE_ANIMATION_DURATION}s ease-in-out;
            `}
          />
        </form>
      </Box>
    </Flex>
  );
};

const SearchResultItem = ({
  hit,
  index,
}: {
  hit: SearchResult;
  index: string;
}) => {
  const imageURL = makeResizedUrl(hit.image, {
    width: MAX_PRODUCT_IMG_SIZE,
    height: MAX_PRODUCT_IMG_SIZE,
    crop: 'faces,entropy',
    fit: 'clamp',
  });

  const queryParams = hit.queryID
    ? [
        { name: config.algolia.paramIndexName, value: index },
        { name: config.algolia.paramQueryIdName, value: hit.queryID },
      ]
    : undefined;

  const postInsights = usePostInsights();

  return (
    <Grid
      gridRowGap={1}
      gridColumnGap={0}
      data-pid={hit.objectID}
      data-unbxd-identifier={UnbxdAnalyticsIdentifiers.product}
      className={`algolia-search-result ${UnbxdAnalyticsIdentifiers.product}`}
      css={css`
        height: 100%;
        width: 100%;
        /* IOS 12is blank scroll mitigation*/
        -webkit-transform: translate3d(0, 0, 0);
        -webkit-perspective: 1000;
      `}
    >
      <Link
        dynamicUrl="/products/[id]"
        href={`/products/${hit.id}${
          queryParams
            ? `?${queryParams
                .map(
                  ({ name, value }) =>
                    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
                )
                .join('&')}`
            : ''
        }`}
        css={theme => css`
          backface-visibility: hidden;

          ${productHoverStyle(theme)};
        `}
        onClick={() =>
          hit.queryID &&
          hit.objectID &&
          postInsights({
            queryID: hit.queryID,
            objectID: hit.objectID,
            index,
            eventType: 'click',
            eventName: 'Product clicked on search overlay',
            position: hit.searchIndexPosition,
          })
        }
      >
        <Flex
          alignItems="flex-start"
          css={theme => css`
            border-radius: ${theme.radii[3]}px;
            box-shadow: ${theme.shadows.products};
            transition: all 0.2s ease;

            will-change: transform;

            &:after {
              content: '';
              display: block;
              padding-bottom: 100%;
            }
            &:hover {
              .soldOutWrapper {
                animation-name: fadeOut;
              }
            }

            .soldOutWrapper {
              ${soldOutWrapperStyle}
              animation-duration: 500ms;
              animation-fill-mode: both;
              animation-name: fadeIn;
            }
          `}
        >
          <>
            <img
              className="image"
              src={imageURL}
              alt={hit.name}
              title={hit.name}
              css={theme => css`
                border-radius: ${theme.radii[3]}px;
                user-drag: none;
                -webkit-user-drag: none;
                user-select: none;
                -moz-user-select: none;
                -webkit-user-select: none;
                -ms-user-select: none;
                display: block;
                width: 100%;
                height: 100%;
                opacity: 1;
                transition-duration: 512ms;
                transition-property: opacity, visibility;
                transition-timing-function: ease-out;
                visibility: visible;
              `}
            />
            {/*  Overlay to prevent the user from right-clicking and save the image */}
            <Box
              zIndex={10}
              position="absolute"
              top="0"
              left="0"
              minWidth="100%"
              minHeight="100%"
            />
            {hit.isSoldOut && (
              <Box className="soldOutWrapper">
                <SoldOutMask isDark text headingFontSize={[3, 4]} isOnSearch />
              </Box>
            )}
          </>
        </Flex>

        <Grid
          gridTemplateRows="auto 1fr"
          gridColumnGap={0}
          gridRowGap={0}
          mt={3}
          css={css`
            overflow: hidden;
          `}
        >
          <Heading
            fontSize={['0.9rem', '1.1rem', 2]}
            fontWeight="bold"
            lineHeight="1.2em"
            color="black"
          >
            {hit.brand}
          </Heading>
          <Heading
            fontWeight="medium"
            fontSize={['0.8rem', '1.1rem', 2]}
            color="darkGrey"
            lineHeight="1.5em"
            css={css`
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {hit.shortName || hit.name}
          </Heading>
        </Grid>

        <Flex mb={2} alignItems="flex-end" flexWrap="wrap">
          <Heading
            fontWeight={600}
            mr={2}
            mt={2}
            color="black"
            className="highlightOnHover"
            css={css`
              position: relative;
              bottom: -0.05em;
            `}
          >
            {hit.price}
          </Heading>

          {!!hit.formattedRetail && hit.formattedRetail !== 'R0' && (
            <Heading
              fontSize={2}
              color="darkGrey"
              fontWeight="medium"
              mt={2}
              css={css`
                text-decoration: line-through;
              `}
            >
              {hit.formattedRetail}
            </Heading>
          )}
        </Flex>
      </Link>
    </Grid>
  );
};

const SearchResultsGallery = ({
  showMobileFilters,
  index,
  showPagination,
  loading,
  numHits,
}: {
  showMobileFilters: () => void;
  index: string;
  showPagination: boolean;
  loading: boolean;
  numHits: number;
}) => {
  const { items } = useHits<AlgoliaSearchResult>();

  const validItems = useMemo(
    () => items.filter(isValidHit).map(transformSearchResult),
    [items]
  );

  return (
    <Box pt={3}>
      <Box width="100%">
        <Flex
          mb={3}
          pb={[2, 3]}
          width="100%"
          justifyContent="space-between"
          alignItems={[null, 'center']}
          css={theme => css`
            border-bottom: 1px solid ${theme.colors.lightishGrey};
          `}
        >
          <Heading fontSize={['0.9rem', 'm']}>
            {numHits === 1
              ? '1 Product found'
              : numHits === 0
              ? 'No Products found'
              : `${showPagination ? numHits : items.length} Products found`}
          </Heading>
          {showPagination && (
            <RenderControl type="client" screen="tabletUp">
              <CustomPagination padding={2} />
            </RenderControl>
          )}

          <Button
            className="show-for-mobile-only"
            variant="flat"
            p={0}
            m={0}
            textTransform="none"
            onClick={showMobileFilters}
          >
            <FilterIcon />
            <Heading
              ml={2}
              fontSize="s"
              color="blue"
              fontFamily="header"
              fontWeight="medium"
            >
              Filter
            </Heading>
          </Button>
        </Flex>
      </Box>

      {items.length > 0 ? (
        <Loading isLoading={loading} iconTop={100}>
          <Grid
            width="100%"
            pb={['160px', 3]}
            gridColumnGap={4}
            gridTemplateColumns={[
              'repeat(2, 1fr)',
              'repeat(3, 1fr)',
              'repeat(5, 1fr)',
            ]}
          >
            {validItems.map((item, idx) => (
              <SearchResultItem key={idx} hit={item} index={index} />
            ))}
          </Grid>
        </Loading>
      ) : (
        <Text lineHeight={1.5}>
          No search results found, please try a different search or check out
          our other deals.
        </Text>
      )}
    </Box>
  );
};

let timeoutId: NodeJS.Timeout | undefined;

export const SearchResultsOverlay = ({
  index,
  isCategoriesVisible,
}: {
  index: string;
  isCategoriesVisible?: boolean;
}) => {
  const { items, results } = useHits();
  const numHits = results?.nbHits || 0;

  const { events } = useRouter();
  const activeDrawer = !!useSelector(
    (state: RootState) => state.global.openDrawer
  );

  const showPagination = (results?.nbPages || 0) > 1;
  const [loading, setLoading] = useState(false);
  const { addMiddlewares } = useInstantSearch();
  const [error, setError] = useState(null);

  const [isMobileFiltersActive, setIsMobileFiltersActive] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const openSearch = useCallback(() => setIsSearchActive(true), []);
  const closeSearch = useCallback(() => setIsSearchActive(false), []);

  useEffect(() => {
    if (isSearchActive) {
      document.body.classList.add('noscroll');
    }

    return () => {
      document.body.classList.remove('noscroll');
    };
  }, [isSearchActive]);

  // timeout to show loading spinner when search results change
  useEffect(() => {
    setLoading(true);
    timeoutId = setTimeout(() => {
      setLoading(false);
    }, 700);
    return () => timeoutId && clearTimeout(timeoutId);
  }, [items]);

  useEffect(() => {
    activeDrawer && closeSearch();
  }, [activeDrawer, closeSearch]);

  useEffect(() => {
    events.on('routeChangeComplete', () => closeSearch());
  }, [closeSearch, events]);

  /**
   * Middleware function to handle errors from the instantSearchInstance.
   *
   * This middleware subscribes to the 'error' event of the instantSearchInstance
   * and sets the error state when an error occurs. This defends us from any issues that we
   * cannot predict from Algolia as a whole.
   *
   * instantSearchInstance - The instance of the InstantSearch.
   * This useEffect returns an object with subscribe and unsubscribe methods for managing the error listener.
   */
  useEffect(() => {
    const middleware = ({ instantSearchInstance }) => {
      const handleError = searchError => setError(searchError);

      return {
        subscribe: () => {
          instantSearchInstance.addListener('error', handleError);
        },
        unsubscribe: () => {
          instantSearchInstance.removeListener('error', handleError);
        },
      };
    };

    return addMiddlewares(middleware);
  }, [addMiddlewares]);

  return (
    <>
      <Box>
        <Box
          mt={['unset', null, isCategoriesVisible ? '20px' : '0']}
          css={css`
            transition: margin-top 0.2s ease-in-out;
          `}
          pb={[2, null, 'unset']}
        >
          <SearchBar
            isSearchActive={isSearchActive}
            closeSearch={closeSearch}
            openSearch={openSearch}
          />
        </Box>
        {isSearchActive && (
          <>
            <Box
              className="hide-for-mobile-only"
              height="100vh"
              width="100%"
              position="absolute"
              left={0}
              bg="overlayBackground"
              onClick={closeSearch}
              css={css`
                cursor: pointer;
              `}
              top={HEADER_SPACING_HEIGHT_W_SEARCH}
            />

            <Flex
              flexDirection={['column', null, 'unset']}
              justifyContent="center"
              backgroundColor="white"
              width="100vw"
              position="absolute"
              boxShadow="cards"
              left={0}
              top={HEADER_SPACING_HEIGHT_W_SEARCH}
            >
              {error ? (
                <SearchErrorFallback />
              ) : (
                <Grid
                  gridTemplateColumns={['1fr', '3fr 1fr', '4fr 1fr']}
                  height={['90vh', '75vh']}
                  width="100%"
                  mx="auto"
                  backgroundColor="white"
                  css={theme => css`
                    max-width: ${theme.maxGridWidth}px;
                    @media ${theme.mediaQueries.tabletDown} {
                      ::-webkit-scrollbar {
                        /*Remove scrollbar on mobile devices*/
                        width: 0;
                        background: transparent;
                      }
                    }
                  `}
                >
                  <Flex
                    overflowY="scroll"
                    width="100%"
                    height={['90vh', '75vh']}
                    flexDirection="column"
                    pb="20px"
                  >
                    <Container py={null} m={0} px={3}>
                      <SearchResultsGallery
                        numHits={numHits}
                        loading={loading}
                        showPagination={showPagination}
                        index={index}
                        showMobileFilters={() => setIsMobileFiltersActive(true)}
                      />

                      {showPagination && (
                        <RenderControl type="client" screen="tabletUp">
                          <Flex width="100%" justifyContent="center" mb="20px">
                            <CustomPagination padding={2} />
                          </Flex>
                        </RenderControl>
                      )}
                    </Container>
                    {showPagination && (
                      <RenderControl type="client" screen="mobileDown">
                        <Flex
                          justifyContent="center"
                          position="fixed"
                          bottom={0}
                          left={0}
                          width="100vw"
                          p="14px"
                          backgroundColor="white"
                          boxShadow="cards"
                        >
                          <CustomPagination padding={1} />
                        </Flex>
                      </RenderControl>
                    )}
                  </Flex>

                  <RenderControl type="client" screen="tabletUp">
                    <SearchFilters />
                  </RenderControl>
                </Grid>
              )}
            </Flex>
          </>
        )}
      </Box>

      <RenderControl type="client" screen="mobileDown">
        <SearchFiltersMobile
          isMobileFiltersOpen={isMobileFiltersActive}
          hideMobileFilters={() => setIsMobileFiltersActive(false)}
        />
      </RenderControl>
    </>
  );
};

const searchClient = liteClient(config.algolia.appId, config.algolia.apiKey);

const Wrapper = ({
  searchIndex,
  isCategoriesVisible,
}: {
  searchIndex?: string;
  isCategoriesVisible?: boolean;
}) => {
  const { isAuthenticated, userToken, authenticatedToken } = useGetTokens();

  const insightsParams = useMemo(
    () => ({
      insightsInitParams: {
        ...(isAuthenticated
          ? { authenticatedUserToken: authenticatedToken }
          : {}),
        userToken,
      },
    }),
    [isAuthenticated, authenticatedToken, userToken]
  );

  if (!searchIndex) return null;

  return (
    <InstantSearch
      indexName={searchIndex}
      searchClient={searchClient}
      insights={insightsParams}
    >
      <SearchConfig
        clickAnalytics
        analytics
        enablePersonalization={config.algolia.enablePersonalisation}
        userToken={isAuthenticated ? authenticatedToken : userToken}
      />

      <ErrorBoundary errorDisplay={<SearchErrorFallback />}>
        <SearchResultsOverlay
          index={searchIndex}
          isCategoriesVisible={isCategoriesVisible}
        />
      </ErrorBoundary>
    </InstantSearch>
  );
};

export default Wrapper;
